import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface SearchListing {
  id: number;
  title: string;
  description: string;
  price: number;
  currency: string;
  location: string;
  category: string;
  subcategory: string;
  table_name: string;
  status: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  is_featured: boolean;
  has_images: boolean;
  image_count: number;
  first_image: string | null;
  images: string[];
  user_id?: number;
  seller: {
    id?: number;
    first_name: string;
    last_name: string;
    user_id?: number;
  };
}

export interface SearchPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface SearchFilters {
  [key: string]: any;
  price_min?: number;
  price_max?: number;
  has_images?: boolean;
  location?: string;
  city?: string;
  subcategory?: string;
  brand?: string;
  model?: string;
  year_min?: number;
  year_max?: number;
  condition?: string;
  rooms_min?: number;
  rooms_max?: number;
  square_meters_min?: number;
  square_meters_max?: number;
  bedrooms_min?: number;
  bedrooms_max?: number;
  bathrooms_min?: number;
  construction_year_min?: number;
  construction_year_max?: number;
  monthly_rent_min?: number;
  monthly_rent_max?: number;
  budget_max_min?: number;
  budget_max_max?: number;
  base_price_min?: number;
  base_price_max?: number;
  experience_years_min?: number;
  experience_years_max?: number;
  purchase_year_min?: number;
  purchase_year_max?: number;
  mileage_max?: number;
  fuel?: string;
  transmission?: string;
  body_type?: string;
  doors?: number;
  seats_min?: number;
  engine_size_min?: number;
  engine_size_max?: number;
  power_kw_min?: number;
  power_kw_max?: number;
  power_hp_min?: number;
  power_hp_max?: number;
  drive_type?: string;
  first_registration_from?: string;
  first_registration_to?: string;
  owners_count_max?: number;
  color?: string;
  euro_emission_class?: number;
  furnished?: string;
  heating?: string;
  energy_class?: string;
  garage?: boolean;
  garden?: boolean;
  basement?: boolean;
  balcony?: boolean;
  terrace?: boolean;
  pets_allowed?: boolean;
  utilities_included?: boolean;
  deposit_max?: number;
  utilities_max?: number;
  min_lease_months_max?: number;
  available_from?: string;
  postal_code?: string;
  remote_available?: boolean;
  travel_willing?: boolean;
  max_travel_distance_max?: number;
  weekend_available?: boolean;
  emergency_service?: boolean;
  licensed_insured?: boolean;
  own_tools_equipment?: boolean;
  languages_spoken?: string;
  certifications?: string;
  availability?: string;
  start_availability?: string;
  service_radius_max?: number;
  portfolio_available?: boolean;
  references_available?: boolean;
}

export interface SearchResult {
  listings: SearchListing[];
  pagination: SearchPagination;
  search: {
    keyword: string;
    category: string;
    subcategory?: string;
    filters: SearchFilters;
  };
}

export interface SearchResponse {
  status: string;
  data: SearchResult;
}

export interface FilterOption {
  name: string;
  type: string;
  label: string;
  options?: any[];
  min?: number;
  max?: number;
  step?: number;
}

export interface FilterOptionsResponse {
  status: string;
  data: {
    category: string;
    subcategory?: string;
    filters: {
      sort_options: Array<{ value: string; label: string }>;
      common_filters: FilterOption[];
      specific_filters?: FilterOption[];
    };
  };
}

export interface SubcategoriesResponse {
  status: string;
  data: {
    category: string;
    subcategories: Array<{
      key: string;
      label: string;
    }>;
  };
}

export interface SuggestedListingsResponse {
  status: string;
  data: {
    suggestions: SearchListing[];
    count: number;
    metadata: {
      excludedListingId: number | string;
      category: string;
      subcategory?: string;
      planType: string;
      requestedLimit: number;
      actualReturned: number;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';

  constructor(private http: HttpClient) {}

  universalSearch(
    keyword: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    const isBrowseMode = !keyword || keyword.trim().length === 0;
    const urlKeyword = isBrowseMode ? 'browse' : encodeURIComponent(keyword);

    return this.http
      .get<SearchResponse>(`${this.apiUrl}/search/category/all/${urlKeyword}`, {
        params,
      })
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  categorySearch(
    category: string,
    keyword: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    const isBrowseMode = !keyword || keyword.trim().length === 0;
    const urlKeyword = isBrowseMode ? 'browse' : encodeURIComponent(keyword);

    const url =
      category === 'all'
        ? `${this.apiUrl}/search/category/all/${urlKeyword}`
        : `${this.apiUrl}/search/category/${category}/${urlKeyword}`;

    return this.http.get<SearchResponse>(url, { params }).pipe(
      map((response) => response.data),
      catchError(this.handleError)
    );
  }

  subcategorySearch(
    category: string,
    subcategory: string,
    keyword: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    const isBrowseMode = !keyword || keyword.trim().length === 0;
    const urlKeyword = isBrowseMode ? 'browse' : encodeURIComponent(keyword);

    return this.http
      .get<SearchResponse>(
        `${this.apiUrl}/search/subcategory/${category}/${subcategory}/${urlKeyword}`,
        { params }
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getSubcategories(
    category: string
  ): Observable<SubcategoriesResponse['data']> {
    return this.http
      .get<SubcategoriesResponse>(
        `${this.apiUrl}/search/subcategories/${category}`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getFilterOptions(
    category: string,
    subcategory?: string
  ): Observable<FilterOptionsResponse['data']> {
    const url = subcategory
      ? `${this.apiUrl}/search/filters/${category}/${subcategory}`
      : `${this.apiUrl}/search/filters/${category}`;

    return this.http.get<FilterOptionsResponse>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError)
    );
  }

  browseCategoryListings(
    category: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http
      .get<SearchResponse>(`${this.apiUrl}/search/browse/${category}`, {
        params,
      })
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  browseSubcategoryListings(
    category: string,
    subcategory: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http
      .get<SearchResponse>(
        `${this.apiUrl}/search/browse/${category}/${subcategory}`,
        { params }
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  search(
    keyword: string,
    category?: string,
    subcategory?: string,
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    const isBrowseMode =
      !keyword ||
      keyword.trim().length === 0 ||
      keyword === '*' ||
      keyword === 'browse';

    if (isBrowseMode) {
      if (category && category !== 'all' && subcategory) {
        return this.browseSubcategoryListings(
          category,
          subcategory,
          filters,
          page,
          limit,
          sortBy
        );
      } else if (category && category !== 'all') {
        return this.browseCategoryListings(
          category,
          filters,
          page,
          limit,
          sortBy
        );
      } else {
        return this.universalSearch('', filters, page, limit, sortBy);
      }
    }

    const cleanKeyword = keyword.trim();

    if (subcategory && category) {
      return this.subcategorySearch(
        category,
        subcategory,
        cleanKeyword,
        filters,
        page,
        limit,
        sortBy
      );
    } else if (category && category !== 'all') {
      return this.categorySearch(
        category,
        cleanKeyword,
        filters,
        page,
        limit,
        sortBy
      );
    } else {
      return this.universalSearch(cleanKeyword, filters, page, limit, sortBy);
    }
  }

  getSuggestedListings(
    listingId: number | string,
    category?: string,
    subcategory?: string,
    limit: number = 6,
    excludeUserId?: number | string
  ): Observable<SuggestedListingsResponse['data']> {
    let params = new HttpParams().set('limit', limit.toString());

    if (category) {
      params = params.set('category', category);
    }
    if (subcategory) {
      params = params.set('subcategory', subcategory);
    }
    if (excludeUserId) {
      params = params.set('exclude_user_id', excludeUserId.toString());
    }

    const listingIdString = listingId.toString();

    return this.http
      .get<SuggestedListingsResponse>(
        `${this.apiUrl}/search/suggested/${listingIdString}`,
        { params }
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  formatPrice(price: number, currency: string = 'EUR'): string {
    if (price === 0) {
      return 'Free';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }

  getCategoryDisplayName(category: string): string {
    const categoryNames: { [key: string]: string } = {
      all: 'All Categories',
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryNames[category] || category;
  }

  getSubcategoryDisplayName(subcategory: string): string {
    return subcategory
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  isEmptyResult(result: SearchResult): boolean {
    return !result.listings || result.listings.length === 0;
  }

  getDefaultImage(category: string): string {
    const colors: { [key: string]: string } = {
      vehicles: '#3b82f6',
      'real-estate': '#10b981',
      marketplace: '#f59e0b',
      jobs: '#8b5cf6',
    };

    const color = colors[category] || '#6b7280';
    const categoryName = this.getCategoryDisplayName(category);

    const svg = `
      <svg width="320" height="240" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="240" fill="${color}20"/>
        <rect x="0" y="0" width="320" height="240" fill="none" stroke="${color}" stroke-width="2" stroke-dasharray="8,4"/>
        <text x="160" y="110" text-anchor="middle" font-size="14" fill="${color}" font-weight="500">${categoryName}</text>
        <text x="160" y="135" text-anchor="middle" font-size="12" fill="${color}80">No Image</text>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Search failed';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 400) {
      errorMessage = 'Invalid search parameters';
    } else if (error.status === 429) {
      errorMessage = 'Too many search requests. Please try again later.';
    } else if (error.status === 500) {
      errorMessage = 'Search service temporarily unavailable';
    }

    console.error('Search service error:', error);
    return throwError(() => new Error(errorMessage));
  }

  getRecentListings(
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http
      .get<SearchResponse>(`${this.apiUrl}/search/recent`, { params })
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getFeaturedListings(
    filters: SearchFilters = {},
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'newest'
  ): Observable<SearchResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          params = params.set(key, value.toString());
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http
      .get<SearchResponse>(`${this.apiUrl}/search/featured`, { params })
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }
}
